import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useMutation} from '@apollo/client'
import {Link, useLocation} from 'react-router-dom'
import {useState} from 'react'
import {toast} from 'react-toastify'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {VERSION_APP} from '../../helpers/consts'
import {REQUEST_PASSWORD_RESET} from '../../gql/mutations/authMutations'

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
})

export const ForgotPasswordPage = () => {
  const defaultLogo = toAbsoluteUrl('/media/tfi/logo_policytools.png')
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const prefilledEmail = params.get('email') || ''
  const isForcedFlow = params.get('forced') === '1'

  const [requestReset, {loading}] = useMutation(REQUEST_PASSWORD_RESET)
  const [sent, setSent] = useState(false)

  const formik = useFormik({
    initialValues: {
      email: prefilledEmail,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await requestReset({variables: {email: values.email}})
        const message =
          response.data?.requestPasswordReset?.message ||
          'If the email exists, a reset link has been sent.'
        toast.success(message)
        setSent(true)
      } catch (error) {
        toast.error('An error occurred. Please try again.')
        setSent(false)
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-lg-row flex-column-fluid h-100'>
      <div className='d-flex flex-column flex-lg-row-fluid w-lg-50 p-10 order-2 order-lg-1'>
        <div className='d-flex flex-center flex-column flex-lg-row-fluid'>
          <div className='w-lg-500px p-10'>
            <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
              <div className='text-center mb-11'>
                <img src='/media/tfi/logoNColorP.png' width={200} alt='logo' />
                <h1 className='text-dark fw-bolder mb-3'>Forgot Password</h1>
                <div className='text-gray-500 fw-semibold fs-6'>
                  Enter your email to receive a reset link.
                </div>
              </div>

              {isForcedFlow && (
                <div
                  className='mb-6 alert'
                  style={{
                    backgroundColor: '#FFF4E5',
                    color: '#7A2E00',
                    border: '1px solid #C25100',
                  }}
                >
                  You must change your password before signing in.
                </div>
              )}

              {sent && (
                <div className='mb-10 bg-light-info p-8 rounded'>
                  <div className='text-info'>
                    If the email exists, a reset link has been sent.
                  </div>
                </div>
              )}

              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
                <input
                  placeholder='Email'
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    'form-control bg-transparent',
                    {'is-invalid': formik.touched.email && formik.errors.email},
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                  type='email'
                  name='email'
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.email}
                    </span>
                  </div>
                )}
              </div>

              <div className='d-grid mb-10'>
                <button
                  type='submit'
                  className='btn btn-primary mt-4'
                  disabled={formik.isSubmitting || !formik.isValid || loading}
                >
                  {!loading && <span className='indicator-label'>Send reset link</span>}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>

              <div className='text-gray-500 text-center fw-semibold fs-6'>
                <Link to='/login' className='link-primary'>
                  Back to login
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className='d-flex flex-center flex-wrap px-5'>
          <div>
            <span>{VERSION_APP}</span>
          </div>
        </div>
      </div>

      <div
        className='d-flex flex-lg-row-fluid w-lg-50 bgi-size-cover bgi-position-center order-1 order-lg-2'
        style={{backgroundImage: `url(${toAbsoluteUrl('/media/misc/auth-bg.png')})`}}
      >
        <div className='d-flex flex-column flex-center py-15 px-5 px-md-15 w-100'>
          <span className='mb-12'>
            <img alt='Logo' src={defaultLogo} className='h-75px' />
          </span>
          <img
            className='mx-auto w-275px w-md-60 w-xl-500px mb-10 mb-lg-20'
            src={toAbsoluteUrl('/media/tfi/login.png')}
            alt=''
          />
          <h1 className='text-white fs-2qx fw-bolder text-center mb-7'>
            Fast, Efficient and Productive
          </h1>
        </div>
      </div>
    </div>
  )
}
